import Service from '@ember/service';
import { task } from 'ember-concurrency';
import { timeout } from 'lumen/utils/waiters';

export const RESIZE_EVENTS = {
  MODAL: 'modal',
  FILTER_BAR: 'filter-bar',
  NAVIGATION: 'navigation',
} as const;

type ResizeEventKey = (typeof RESIZE_EVENTS)[keyof typeof RESIZE_EVENTS];

export default class ResizeService extends Service {
  // Preserve a reference to the event handler for proper cleanup
  #performResizeReference: (() => void) | null = null;
  #resizeEvents: Map<ResizeEventKey, () => void> = new Map();

  resizeHandlerTask = task({ restartable: true }, async (): Promise<void> => {
    await timeout();
    this.#resizeEvents.forEach((resizeEvent) => {
      resizeEvent();
    });
  });

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.#performResizeReference = this.performResize.bind(this);
    window.addEventListener('resize', this.#performResizeReference);
  }

  willDestroy(...args: Parameters<Service['willDestroy']>): void {
    super.willDestroy(...args);
    if (this.#performResizeReference) {
      window.removeEventListener('resize', this.#performResizeReference);
      this.#performResizeReference = null;
      this.#resizeEvents.clear();
    }
  }

  performResize(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.resizeHandlerTask.perform();
  }

  registerResizeEvent(
    resizeEventKey: ResizeEventKey,
    resizeEvent: () => void,
  ): void {
    this.#resizeEvents.set(resizeEventKey, resizeEvent);
  }

  unregisterResizeEvent(resizeEventKey: ResizeEventKey): void {
    this.#resizeEvents.delete(resizeEventKey);
  }
}

declare module '@ember/service' {
  interface Registry {
    resize: ResizeService;
  }
}
